import { useEffect, useRef } from 'react';
import { useLocation } from 'react-router-dom';
import analyticsService from '../services/analyticsService';
import firstPartyAnalytics from '../services/firstPartyAnalytics';
import { AnalyticsEvents, classifyExternalLink, contentFromPath, hostOf, isExternalHref } from '../services/analyticsEvents';
import { useAuth } from '../contexts/AuthContext';

/**
 * PURE: Klick auf einen externen Link → Event-Parameter oder null.
 * Sponsor-Banner tragen data-no-external-track (eigenes Sponsor-Event).
 */
export const externalLinkEventFromClick = (
  target: EventTarget | null,
  pathname: string,
): Record<string, unknown> | null => {
  const el = target as HTMLElement | null;
  const anchor = el?.closest?.('a') as HTMLAnchorElement | null;
  if (!anchor) return null;
  const href = anchor.getAttribute('href');
  if (!isExternalHref(href) || anchor.closest('[data-no-external-track]')) return null;
  const hint = `${anchor.getAttribute('aria-label') ?? ''} ${anchor.textContent ?? ''} ${anchor.getAttribute('data-link-kind') ?? ''}`;
  const ctx = contentFromPath(pathname);
  return { url_host: hostOf(href), link_kind: classifyExternalLink(href!, hint), content_type: ctx.content_type, content_id: ctx.content_slug };
};

/**
 * SPA-Pageview-Tracking: meldet jeden Routenwechsel als page_view an GA4
 * UND (consent-unabhängig, cookieless) ans eigene Backend.
 * Muss INNERHALB des Routers UND des AuthProviders gemountet sein.
 * Rendert nichts.
 *
 * Beim ersten Mount wird init() versucht — hat der Nutzer früher schon
 * eingewilligt, startet GA damit auch ohne erneute Banner-Interaktion.
 * Alle Consent-/ID-Guards sitzen im analyticsService; das First-Party-
 * Tracking braucht keinen Consent (keine persistente Client-ID).
 */
const AnalyticsTracker: React.FC = () => {
  const location = useLocation();
  const { user } = useAuth();

  // User-ID als Ref: Login/Logout soll KEINEN zusätzlichen page_view
  // auslösen — nur echte Routenwechsel tracken.
  const userIdRef = useRef<string | null>(null);
  userIdRef.current = user?.id ?? null;

  useEffect(() => {
    analyticsService.init();
  }, []);

  // Externe Links (Website, Shop, Tickets …) zentral tracken (2026-09-22)
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      const params = externalLinkEventFromClick(e.target, window.location.pathname);
      if (params) void firstPartyAnalytics.trackEvent(AnalyticsEvents.externalLinkClick, params);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);

  useEffect(() => {
    // Titel setzt react-helmet asynchron — kurz warten, damit der
    // page_view den richtigen Seitentitel trägt.
    const timer = window.setTimeout(() => {
      const path = location.pathname + location.search;
      analyticsService.trackPageView(path);
      firstPartyAnalytics.trackPageView(path, document.title, userIdRef.current);
    }, 300);
    return () => window.clearTimeout(timer);
  }, [location.pathname, location.search]);

  return null;
};

export default AnalyticsTracker;
